// Global modal focus manager — WCAG 2.4.3 / 2.1.2 for every dialog that
// declares itself modal.
//
// Every overlay in the app that sets `aria-modal="true"` gets, with zero
// per-dialog wiring:
//   1. Tab / Shift-Tab confined to the dialog (wrapping at the edges),
//   2. stray focus (click, script, autofill) pulled back inside, and
//   3. focus restored to the element that had it when the dialog opened.
//
// Visibility follows the codebase-wide convention: dialogs are shown and
// hidden by toggling the `hidden` class (or inline display). The `hidden`
// attribute, `inert` and a stylesheet's display:none or visibility:hidden
// also count as hidden, so a modal the game room has made inert behind its
// own layer never traps Tab. A dialog the manager should handle MUST carry
// aria-modal="true" — overlays without it (toasts, HUD prompts) are
// deliberately left alone.
//
// Escape handling stays per-dialog: closing has side effects (form resets,
// state clears) that each owner already implements.

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

let _installed = false;
let _observer = null;
let _visibleModals = new Set();
const _openers = new Map();

export function isDisplayed(el, win = globalThis) {
  if (!el || el.isConnected !== true) return false;
  const view = typeof win?.getComputedStyle === 'function' ? win : globalThis;
  for (let n = el; n; n = n.parentElement) {
    if (n.hasAttribute('hidden') || n.classList.contains('hidden')) return false;
  }
  for (let n = el; n; n = n.parentElement) {
    const style = view.getComputedStyle(n);
    if (style.display === 'none' || style.visibility === 'hidden' || style.visibility === 'collapse') return false;
  }
  return true;
}

// X3: dialogs and focusables must be displayed along their whole ancestor
// chain (X-DISPLAYED) and outside any aria-hidden or inert subtree (K9).
// Cheap attribute checks precede isDisplayed's computed-style reads.
function trapShown(el) {
  for (let n = el; n; n = n.parentElement) {
    if (n.getAttribute?.('aria-hidden') === 'true') return false;
    if (n.hasAttribute?.('inert') || n.inert === true) return false;
  }
  return isDisplayed(el);
}

function visibleModalList() {
  return [...document.querySelectorAll('[aria-modal="true"]')].filter(trapShown);
}

// Topmost = last visible modal in document order. Matches how the app
// stacks overlays (later siblings paint above earlier ones).
export function activeModal() {
  const list = visibleModalList();
  return list.length ? list[list.length - 1] : null;
}

export function focusableIn(container) {
  return [...container.querySelectorAll(FOCUSABLE_SELECTOR)].filter(trapShown);
}

function onKeydown(e) {
  if (e.key !== 'Tab') return;
  const modal = activeModal();
  if (!modal) return;
  const focusables = focusableIn(modal);
  if (!focusables.length) { e.preventDefault(); return; }
  const first = focusables[0];
  const last = focusables[focusables.length - 1];
  const current = document.activeElement;
  if (!modal.contains(current)) {
    e.preventDefault();
    (e.shiftKey ? last : first).focus();
    return;
  }
  if (!e.shiftKey && current === last) {
    e.preventDefault();
    first.focus();
  } else if (e.shiftKey && current === first) {
    e.preventDefault();
    last.focus();
  }
}

function onFocusin(e) {
  const modal = activeModal();
  if (!modal) return;
  const target = e.target;
  if (modal.contains(target)) return;
  // Focus escaped (click on background, programmatic focus, autofill).
  const focusables = focusableIn(modal);
  if (focusables.length) focusables[0].focus();
}

export const MODAL_SURFACES_CHANGED_EVENT = 'retroplay:modal-surfaces-changed';

function syncVisibleModals() {
  const now = new Set(visibleModalList());
  const changed = now.size !== _visibleModals.size || [...now].some((el) => !_visibleModals.has(el));
  // Newly opened → remember who had focus so close can hand it back.
  for (const m of now) {
    if (!_visibleModals.has(m)) {
      const opener = document.activeElement;
      _openers.set(m, opener && opener !== document.body && !m.contains(opener) ? opener : null);
    }
  }
  // Newly closed → restore focus if it died with the dialog.
  for (const m of _visibleModals) {
    if (!now.has(m)) {
      const opener = _openers.get(m);
      _openers.delete(m);
      const act = document.activeElement;
      const focusLost = !act || act === document.body || m.contains(act);
      if (!focusLost) continue;
      const top = activeModal();
      if (top) {
        // Stacked close: hand focus to the dialog underneath — to the
        // opener if it lives there, else its first focusable.
        const target = opener && opener.isConnected && top.contains(opener)
          ? opener
          : focusableIn(top)[0];
        target?.focus();
      } else if (opener && opener.isConnected) {
        opener.focus();
      }
    }
  }
  _visibleModals = now;
  if (changed) window.dispatchEvent(new Event(MODAL_SURFACES_CHANGED_EVENT));
}

export function initModalFocusManager() {
  if (_installed) return;
  _installed = true;
  document.addEventListener('keydown', onKeydown, true);
  document.addEventListener('focusin', onFocusin);
  document.addEventListener('toggle', syncVisibleModals, true);
  _visibleModals = new Set(visibleModalList());
  _observer = new MutationObserver(syncVisibleModals);
  _observer.observe(document.documentElement, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['class', 'style', 'aria-modal', 'hidden', 'inert', 'aria-hidden'],
  });
}

// Test hook — tears everything down so specs run isolated.
export function destroyModalFocusManager() {
  if (!_installed) return;
  _installed = false;
  document.removeEventListener('keydown', onKeydown, true);
  document.removeEventListener('focusin', onFocusin);
  document.removeEventListener('toggle', syncVisibleModals, true);
  _observer?.disconnect();
  _observer = null;
  _visibleModals = new Set();
  _openers.clear();
}
